import { CreditCard } from "lucide-react";
import { cx, FieldLabel, useFormField } from "@/components/ui";
import type { CardErrors } from "@/lib/payments";

import type { CardEntry } from "./useCardEntry";

/** What we ask for ourselves: Stripe's card form doesn't ask for the cardholder's name. */
export interface CardForm {
  name: string;
}

export const emptyCard = (name = ""): CardForm => ({ name });

const inputClass =
  "w-full rounded-[10px] border border-[#d4cac4] bg-surface py-3 pr-3 pl-3.5 text-sm leading-5 text-fg outline-none placeholder:text-[#b3a8a3] focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

function Skeleton() {
  return (
    <div className="flex flex-col gap-3.5" aria-hidden>
      <div className="h-[72px] animate-pulse rounded-[10px] bg-muted" />
      <div className="flex gap-3.5">
        <div className="h-[72px] flex-1 animate-pulse rounded-[10px] bg-muted" />
        <div className="h-[72px] flex-1 animate-pulse rounded-[10px] bg-muted" />
      </div>
    </div>
  );
}

/**
 * Card entry (Figma "Payment details"): Stripe's Payment Element — card number, expiry, security
 * code, country and postcode in Stripe's own secure form — and the name on the card.
 */
export function CardFields({
  entry,
  value,
  onChange,
  errors = {},
}: {
  entry: CardEntry;
  value: CardForm;
  onChange: (next: CardForm) => void;
  errors?: CardErrors;
}) {
  const name = useFormField({ label: "Name on card", name: "cardholder_name", required: true });
  const { provider, secure, ready, loadError } = entry;
  if (provider === "loading") return <Skeleton />;
  if (!secure || loadError) {
    return (
      <p
        role="status"
        className="flex items-start gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted"
      >
        <CreditCard className="mt-0.5 size-[18px] shrink-0" aria-hidden />
        {loadError ??
          "Card payments aren't switched on yet. Stripe's secure card form appears here once Stripe is connected."}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-col gap-1.5">
        {!ready && <Skeleton />}
        <div ref={secure} className={ready ? undefined : "h-0 overflow-hidden"} />
        {errors.card && (
          <p role="alert" className="text-xs text-[#b8291f]">
            {errors.card}
          </p>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <FieldLabel htmlFor={name.id} required className="text-xs leading-4 font-medium text-fg-muted">
          Name on card
        </FieldLabel>
        <input
          {...name.control}
          autoComplete="cc-name"
          placeholder="Riley Jameson"
          maxLength={200}
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
          className={cx(inputClass, name.message && "border-[#b8291f]")}
        />
        {name.message && (
          <p id={name.messageId} className="text-xs text-[#b8291f]">
            {name.message}
          </p>
        )}
      </div>
    </div>
  );
}
